import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Cookie, X } from 'lucide-react';

/**
 * Cookie consent (CMP) — GDPR/ePrivacy-kompatibel.
 *
 * Kategorier:
 * - necessary: altid på (login, sikkerhed, samtykke-gem).
 * - statistics: Sentry/PostHog/GA o.l. — kun loadet hvis samtykket.
 * - marketing: pixels/retargeting — kun loadet hvis samtykket.
 *
 * Samtykke gemmes i localStorage og publiceres via window-event
 * 'consent:change' så analytics-loadere kan reagere.
 */

export type ConsentCategories = {
  necessary: true;
  statistics: boolean;
  marketing: boolean;
};

const STORAGE_KEY = 'consent_v1';
const OPEN_EVENT = 'consent:open';
const CHANGE_EVENT = 'consent:change';

export function getConsent(): ConsentCategories | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      necessary: true,
      statistics: !!parsed.statistics,
      marketing: !!parsed.marketing,
    };
  } catch {
    return null;
  }
}

function saveConsent(c: ConsentCategories) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ statistics: c.statistics, marketing: c.marketing, ts: Date.now() })
    );
    window.dispatchEvent(new CustomEvent<ConsentCategories>(CHANGE_EVENT, { detail: c }));
  } catch {
    // ignore storage errors (e.g. private mode)
  }
}

/** Åbn samtykke-dialogen igen (kaldes fra footer-link). */
export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [statistics, setStatistics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Initial visning: hvis intet samtykke er gemt, vis banneret.
  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setOpen(true);
    } else {
      setStatistics(existing.statistics);
      setMarketing(existing.marketing);
    }
    const onOpen = () => {
      const c = getConsent();
      if (c) {
        setStatistics(c.statistics);
        setMarketing(c.marketing);
      }
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    const c: ConsentCategories = { necessary: true, statistics: true, marketing: true };
    saveConsent(c);
    setOpen(false);
  };

  const rejectAll = () => {
    const c: ConsentCategories = { necessary: true, statistics: false, marketing: false };
    saveConsent(c);
    setOpen(false);
  };

  const saveChoice = () => {
    const c: ConsentCategories = { necessary: true, statistics, marketing };
    saveConsent(c);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie-samtykke"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 pointer-events-none"
    >
      <div className="mx-auto max-w-3xl bg-card text-card-foreground border border-border rounded-xl shadow-2xl p-4 sm:p-5 pointer-events-auto">
        <div className="flex items-start gap-3">
          <Cookie className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-semibold text-base sm:text-lg mb-1">
              Vi bruger cookies
            </h2>
            <p className="text-sm text-muted-foreground">
              Nødvendige cookies sikrer at siden fungerer. Statistik hjælper os med at forbedre
              indholdet, og marketing bruges til relevante anbefalinger. Du kan ændre dit valg
              når som helst via &quot;Cookieindstillinger&quot; i footeren. Læs vores{' '}
              <Link to="/cookies" className="underline hover:text-foreground">cookiepolitik</Link>{' '}
              og{' '}
              <Link to="/persondatapolitik" className="underline hover:text-foreground">persondatapolitik</Link>.
            </p>

            <div className="mt-4 space-y-2.5">
              <Row label="Nødvendige" desc="Login, sikkerhed og dine indstillinger. Kan ikke fravælges." checked disabled />
              <Row
                label="Statistik"
                desc="Måling af brug og fejl med cookies. Uden dit samtykke sendes kun anonyme, cookieløse målinger."
                checked={statistics}
                onChange={setStatistics}
              />
              <Row
                label="Marketing"
                desc="Relevante annoncer og indhold på tværs af tjenester."
                checked={marketing}
                onChange={setMarketing}
              />
            </div>

            <div className="mt-4 flex flex-wrap gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={rejectAll}>
                Afvis alle
              </Button>
              <Button variant="outline" size="sm" onClick={saveChoice}>
                Gem mit valg
              </Button>
              <Button variant="accent" size="sm" onClick={acceptAll}>
                Accepter alle
              </Button>
            </div>
          </div>
          <button
            type="button"
            onClick={rejectAll}
            aria-label="Luk og afvis ikke-nødvendige cookies"
            className="text-muted-foreground hover:text-foreground p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({
  label, desc, checked, disabled, onChange,
}: {
  label: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-t border-border first:border-t-0">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange?.(!!v)}
        aria-label={label}
      />
    </div>
  );
}
